import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { MessageSquareText } from 'lucide-react';
import { useSite } from '../lib/SiteContentContext';
import { resolveImageUrl } from '../lib/imageUrl';

/**
 * Fanned deck of WhatsApp testimonial screenshots.
 *
 * Content is CMS-driven via `useSite().whatsappTestimonials`. Admins add,
 * reorder or unpublish cards straight from /admin/testimonials.
 *
 * Desktop: hovering a card enlarges it in place, opaque and outlined; every
 * other card is dimmed + blurred so the active message reads clearly.
 * Mobile: horizontal snap-scroll that gently auto-advances so parents see
 * multiple reviews without swiping.
 */

// Fallback for the split second between first paint and site-content fetch.
const FALLBACK_CARDS = Array.from({ length: 21 }, (_, i) => ({
  id: `wa-${i + 1}`,
  image: `/testimonials/wa-${i + 1}.${i === 9 || i === 10 ? 'webp' : 'jpeg'}`,
  alt: 'WhatsApp testimonial from a Science Sorted parent or student.',
}));

export default function TestimonialStack() {
  const { whatsappTestimonials } = useSite();
  const cards = useMemo(() => {
    const source = Array.isArray(whatsappTestimonials) && whatsappTestimonials.length
      ? whatsappTestimonials
      : FALLBACK_CARDS;
    return source.map((c) => ({
      id: c.id,
      src: resolveImageUrl(c.image, 600),
      alt: c.alt || 'WhatsApp testimonial from a Science Sorted parent or student.',
    }));
  }, [whatsappTestimonials]);

  const containerRef = useRef(null);
  const [containerW, setContainerW] = useState(1240);
  const [activeIdx, setActiveIdx] = useState(null);

  // Track the actual rendered container width so the fan spread adapts to
  // any viewport (the parent section has overflow-hidden, so a fixed pixel
  // spread was clipping the outermost cards on smaller desktops).
  useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(([entry]) => {
      const w = entry.contentRect.width;
      if (w > 0) setContainerW(w);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Auto-size the fan so all cards fit inside the container without being
  // clipped by the parent section's `overflow-hidden` — including when the
  // edge card is hovered and scaled 1.7x. Reserve HALF a scaled card on each
  // end plus a small breathing margin so the hovered edge card never spills
  // outside the section.
  const CARD_W = 180;
  const HOVER_SCALE = 1.7;
  const stackTransforms = useMemo(() => {
    if (cards.length <= 1) return cards.map(() => ({ tx: 0, rot: 0 }));
    const scaledHalf = (CARD_W * HOVER_SCALE) / 2; // 153
    const usable = Math.max(0, containerW - CARD_W - 2 * scaledHalf - 32);
    const step = Math.min(62, usable / (cards.length - 1));
    const rotStep = 28 / (cards.length - 1);
    return cards.map((_, i) => {
      const offset = i - (cards.length - 1) / 2;
      return { tx: offset * step, rot: offset * rotStep };
    });
  }, [cards.length, containerW]);

  const handleMouseMove = useCallback((e) => {
    const el = containerRef.current;
    if (!el || !stackTransforms.length) return;
    const rect = el.getBoundingClientRect();
    const x = e.clientX - rect.left - rect.width / 2;
    let closest = 0;
    let bestDist = Infinity;
    for (let i = 0; i < stackTransforms.length; i++) {
      const d = Math.abs(stackTransforms[i].tx - x);
      if (d < bestDist) {
        bestDist = d;
        closest = i;
      }
    }
    setActiveIdx(closest);
  }, [stackTransforms]);

  const handleMouseLeave = useCallback(() => setActiveIdx(null), []);

  // --- Mobile auto-advance ---------------------------------------------
  const mobileRowRef = useRef(null);
  const pauseRef = useRef(false);

  useEffect(() => {
    const row = mobileRowRef.current;
    if (!row || cards.length < 2) return undefined;
    const cardWidth = () => (row.firstElementChild?.getBoundingClientRect().width || 0) + 16; // gap-4

    // Pause while the user is interacting so we don't fight their scroll.
    const pause = () => { pauseRef.current = true; };
    const resumeSoon = () => {
      window.clearTimeout(row._resumeTimer);
      row._resumeTimer = window.setTimeout(() => { pauseRef.current = false; }, 3500);
    };
    row.addEventListener('touchstart', pause, { passive: true });
    row.addEventListener('touchend', resumeSoon, { passive: true });
    row.addEventListener('pointerdown', pause);
    row.addEventListener('pointerup', resumeSoon);

    const id = window.setInterval(() => {
      if (pauseRef.current) return;
      if (document.hidden) return;
      const w = cardWidth();
      if (!w) return;
      const nearEnd = row.scrollLeft + row.clientWidth >= row.scrollWidth - 4;
      row.scrollTo({
        left: nearEnd ? 0 : row.scrollLeft + w,
        behavior: 'smooth',
      });
    }, 3500);

    return () => {
      window.clearInterval(id);
      window.clearTimeout(row._resumeTimer);
      row.removeEventListener('touchstart', pause);
      row.removeEventListener('touchend', resumeSoon);
      row.removeEventListener('pointerdown', pause);
      row.removeEventListener('pointerup', resumeSoon);
    };
  }, [cards.length]);

  return (
    <section
      className="py-14 md:py-20 bg-brand-cream relative overflow-hidden"
      aria-labelledby="wa-testimonials-heading"
    >
      <div className="container">
        <div className="max-w-3xl">
          <div className="text-brand-coral text-sm font-semibold uppercase tracking-widest flex items-center gap-2">
            <MessageSquareText className="w-4 h-4" aria-hidden="true" /> In their own words
          </div>
          <h2
            id="wa-testimonials-heading"
            className="mt-2 font-display text-3xl sm:text-4xl md:text-5xl text-brand-ink"
          >
            Real messages. <span className="italic text-brand-teal">Real results.</span>
          </h2>
        </div>
      </div>

      {/* Desktop / tablet - fanned stack. Escapes the narrow `.container`
          so the fan can span the full viewport for comfortable card spacing. */}
      <div className="mt-10 md:mt-14 hidden sm:flex justify-center">
        <div
          ref={containerRef}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
          className="stack-container relative w-full px-4"
          style={{ maxWidth: 1600, height: 560 }}
          data-testid="wa-stack"
        >
          {cards.map((c, i) => {
            const t = stackTransforms[i] || { tx: 0, rot: 0 };
            const isActive = activeIdx === i;
            return (
              <div
                key={c.id || c.src}
                className="stack-card absolute left-1/2 top-1/2 w-[180px] rounded-2xl overflow-hidden transition-all duration-500 ease-out bg-white ring-1 ring-brand-ink/10"
                data-active={isActive ? 'true' : undefined}
                style={{
                  // Card stays at its own tx - does NOT slide to the centre.
                  transform: `translate(-50%, -50%) translateX(${t.tx}px) translateY(${isActive ? -14 : 0}px) rotate(${isActive ? 0 : t.rot}deg) scale(${isActive ? 1.7 : 1})`,
                  zIndex: isActive ? 50 : 20 - Math.abs(i - (cards.length - 1) / 2),
                  // Every card stays fully solid; hover differentiates via scale + shadow only.
                  opacity: 1,
                  backgroundColor: '#ffffff',
                  isolation: isActive ? 'isolate' : 'auto',
                  boxShadow: isActive
                    ? '0 30px 60px -15px rgba(0,0,0,0.55), 0 0 0 2px rgba(0,0,0,0.08), 0 0 0 6px rgba(255,255,255,0.9)'
                    : '0 10px 20px -8px rgba(0,0,0,0.35), 0 0 0 1px rgba(0,0,0,0.06)',
                }}
                data-testid={`wa-card-${i + 1}`}
              >
                <img
                  src={c.src}
                  alt={c.alt}
                  className="block w-full h-auto bg-white"
                  loading="lazy"
                />
              </div>
            );
          })}
        </div>
      </div>

      <div className="container">

        {/* Mobile - horizontal snap scroll with gentle auto-advance */}
        <div className="sm:hidden mt-8 -mx-4 px-4">
          <div
            ref={mobileRowRef}
            className="flex items-start gap-4 overflow-x-auto snap-x snap-mandatory pb-4 scroll-smooth"
            data-testid="wa-mobile-row"
          >
            {cards.map((c, i) => (
              <div
                key={c.id || c.src}
                className="snap-center shrink-0 w-[85vw] max-w-[340px] rounded-2xl overflow-hidden shadow-lg ring-1 ring-brand-ink/10"
                data-testid={`wa-card-m-${i + 1}`}
              >
                <img src={c.src} alt={c.alt} className="block w-full h-auto" loading="lazy" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
